import { useMemo, useState } from "react";
import { useQuery } from "react-query";
import { Download, Eye, Filter, Landmark, Percent, PlayCircle, Plus, RefreshCw, Search, Share2, Users } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  EmptyRows,
  PortalActionButton,
  PortalBadge,
  PortalPageHeader,
  PortalPanel,
  PortalStatCard,
  SummaryLine,
} from "@/components/admin/PortalPrimitives";
import { cn, formatCurrency } from "@/lib/utils";

export default function InterestDistribution() {
  const [search, setSearch] = useState("");

  const financialQuery = useQuery(
    ["interest-distribution-financial"],
    async () => {
      const response = await api.get("/reports/financial");
      return response.data?.data;
    },
    { staleTime: 30000 }
  );

  const membersQuery = useQuery(
    ["interest-distribution-members"],
    async () => {
      const response = await api.get("/reports/members");
      return response.data?.data;
    },
    { staleTime: 30000 }
  );

  const members = membersQuery.data?.members || membersQuery.data?.memberContributions || membersQuery.data?.data || [];
  const inflows = financialQuery.data?.inflows || {};
  const outflows = financialQuery.data?.outflows || {};
  const totalInterest = Number(inflows.penalties || 0);
  const expenses = Number(outflows.expenses || 0);
  const withholding = Math.round(Math.max(0, totalInterest - expenses) * 0.1);
  const distributable = Math.max(0, totalInterest - expenses - withholding);

  const eligibleMembers = useMemo(() => {
    const normalized = Array.isArray(members) ? members : [];
    const withShares = normalized
      .map((member: any) => ({
        id: member.id || member.memberId,
        name: member.memberName || member.fullName || member.fullNames || `${member.firstName || ""} ${member.lastName || ""}`.trim(),
        shares: Number(member.totalShares || member.shares || member.totalSharesBought || member.shareBalance || 0),
        status: member.status || member.membershipStatus || "Eligible",
      }))
      .filter((member: any) => member.name && member.shares > 0);
    const totalShares = withShares.reduce((sum: number, member: any) => sum + member.shares, 0);
    return withShares
      .filter((member: any) => member.name.toLowerCase().includes(search.toLowerCase()))
      .map((member: any) => {
        const sharePct = totalShares > 0 ? member.shares / totalShares : 0;
        const gross = Math.round(distributable * sharePct);
        return { ...member, sharePct, gross, withholding: Math.round(gross * 0.1), net: Math.round(gross * 0.9), totalShares };
      });
  }, [members, distributable, search]);

  const totalShares = eligibleMembers[0]?.totalShares || eligibleMembers.reduce((sum: number, member: any) => sum + member.shares, 0);
  const loading = financialQuery.isLoading || membersQuery.isLoading;

  return (
    <div className="space-y-5 py-4">
      <PortalPageHeader
        title="Interest Distribution"
        description="Distribute accumulated interest to members based on their shares."
        actions={
          <>
            <PortalActionButton variant="outline" onClick={() => financialQuery.refetch()}>
              <RefreshCw className={cn("mr-2 h-4 w-4", financialQuery.isFetching && "animate-spin")} /> Distribution History
            </PortalActionButton>
            <PortalActionButton variant="outline"><Download className="mr-2 h-4 w-4" /> Export</PortalActionButton>
            <PortalActionButton><Plus className="mr-2 h-4 w-4" /> New Distribution</PortalActionButton>
          </>
        }
      />

      <div className="grid gap-5 xl:grid-cols-[1fr_290px]">
        <div className="space-y-5">
          <div className="grid gap-4 md:grid-cols-5">
            <PortalStatCard title="Total Interest Collected" value={formatCurrency(totalInterest)} subtitle="All time collected" icon={<Landmark className="h-5 w-5" />} tone="green" loading={loading} />
            <PortalStatCard title="Distributable Interest" value={formatCurrency(distributable)} subtitle="After deductions" icon={<Percent className="h-5 w-5" />} tone="blue" loading={loading} />
            <PortalStatCard title="Distribution Period" value="Current Period" subtitle="Configurable period" icon={<Share2 className="h-5 w-5" />} tone="amber" loading={loading} />
            <PortalStatCard title="Total Shares Considered" value={totalShares.toLocaleString()} subtitle="Real member shares" icon={<Share2 className="h-5 w-5" />} tone="purple" loading={loading} />
            <PortalStatCard title="Members Eligible" value={eligibleMembers.length} subtitle="Members with shares" icon={<Users className="h-5 w-5" />} tone="amber" loading={loading} />
          </div>

          <PortalPanel title="Distribution Configuration">
            <div className="grid gap-3 lg:grid-cols-[1fr_180px_180px_220px_auto]">
              <Input value="Current reporting period" readOnly />
              <Input value={distributable.toLocaleString()} readOnly />
              <Input type="date" />
              <select className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
                <option>By Shares (Proportional)</option>
              </select>
              <Button className="bg-[#009A44] hover:bg-[#00843a]"><Eye className="mr-2 h-4 w-4" /> Preview Distribution</Button>
            </div>
          </PortalPanel>

          <PortalPanel
            title="Member Interest Distribution Preview"
            action={
              <div className="flex items-center gap-2">
                <div className="relative hidden sm:block">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input value={search} onChange={(event) => setSearch(event.target.value)} className="h-9 w-72 pl-9" placeholder="Search by member name or ID..." />
                </div>
                <Button variant="outline" size="sm"><Filter className="mr-2 h-4 w-4" /> Filter</Button>
              </div>
            }
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-slate-500">
                    <th className="px-3 py-3 font-semibold">#</th>
                    <th className="px-3 py-3 font-semibold">Member</th>
                    <th className="px-3 py-3 text-right font-semibold">Total Shares</th>
                    <th className="px-3 py-3 text-right font-semibold">Share %</th>
                    <th className="px-3 py-3 text-right font-semibold">Interest Amount</th>
                    <th className="px-3 py-3 text-right font-semibold">Withholding (10%)</th>
                    <th className="px-3 py-3 text-right font-semibold">Net Interest</th>
                    <th className="px-3 py-3 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading ? (
                    Array.from({ length: 8 }).map((_, index) => (
                      <tr key={index}><td className="px-3 py-4" colSpan={8}><Skeleton className="h-5 w-full" /></td></tr>
                    ))
                  ) : eligibleMembers.length === 0 ? (
                    <EmptyRows colSpan={8} message="No eligible members with shares were found." />
                  ) : (
                    eligibleMembers.map((member: any, index: number) => (
                      <tr key={member.id || index} className="hover:bg-slate-50/70">
                        <td className="px-3 py-3 text-slate-500">{index + 1}</td>
                        <td className="px-3 py-3 font-semibold text-slate-900">{member.name}</td>
                        <td className="px-3 py-3 text-right font-semibold">{member.shares.toLocaleString()}</td>
                        <td className="px-3 py-3 text-right">{(member.sharePct * 100).toFixed(2)}%</td>
                        <td className="px-3 py-3 text-right font-semibold">{member.gross.toLocaleString()}</td>
                        <td className="px-3 py-3 text-right font-semibold text-red-600">{member.withholding.toLocaleString()}</td>
                        <td className="px-3 py-3 text-right font-semibold text-[#009A44]">{member.net.toLocaleString()}</td>
                        <td className="px-3 py-3"><PortalBadge>{member.status || "Eligible"}</PortalBadge></td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </PortalPanel>
        </div>

        <div className="space-y-5">
          <PortalPanel title="Interest Summary">
            <div className="divide-y divide-slate-100">
              <SummaryLine label="Total Interest Collected" value={totalInterest} money />
              <SummaryLine label="Expenses & Deductions" value={expenses} tone="red" money />
              <SummaryLine label="Distributable Interest" value={distributable} tone="green" money />
              <SummaryLine label="Withholding (10%)" value={withholding} tone="red" money />
              <SummaryLine label="Net to Distribute" value={Math.max(0, distributable - withholding)} tone="green" money />
            </div>
          </PortalPanel>
          <PortalPanel title="Distribution Inputs">
            <div className="divide-y divide-slate-100">
              <SummaryLine label="Members Eligible" value={eligibleMembers.length} />
              <SummaryLine label="Total Shares" value={totalShares.toLocaleString()} />
              <SummaryLine label="Value per Share" value={totalShares > 0 ? formatCurrency(distributable / totalShares) : formatCurrency(0)} tone="green" />
            </div>
          </PortalPanel>
          <PortalPanel title="Quick Actions">
            <div className="space-y-2">
              <Button variant="outline" className="w-full justify-start"><Eye className="mr-2 h-4 w-4 text-[#009A44]" /> Preview Distribution</Button>
              <Button variant="outline" className="w-full justify-start"><PlayCircle className="mr-2 h-4 w-4 text-[#009A44]" /> Execute Distribution</Button>
              <Button variant="outline" className="w-full justify-start"><Download className="mr-2 h-4 w-4 text-[#009A44]" /> Export Report</Button>
            </div>
          </PortalPanel>
        </div>
      </div>
    </div>
  );
}
